'use client';

import { Button } from '@ahafood/ui/components/button';
import { Image } from '@ahafood/ui/components/image';
import { Input } from '@ahafood/ui/components/input';
import { RadioGroup, RadioGroupItem } from '@ahafood/ui/components/radio-group';
import { ScrollArea } from '@ahafood/ui/components/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@ahafood/ui/components/tooltip';
import { useDebouncedValue } from '@ahafood/ui/hooks/use-debounced-value';
import { assetPrefix, type Address } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { format } from 'date-fns';
import { vi } from 'date-fns/locale';
import { Info, LocateFixed, Loader2, MapPin, Sun, User, X } from 'lucide-react';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { useAIPlannerSheet } from '../ai-planner-context';
import { getGoogleAddressFn } from '@/api/ahafood/get-address-detail-fn';
import { useAutocomplete } from '@/api/use-autocomplete';
import { useSaveReminderConfig } from '@/api/use-save-reminder-config';
import { trackEvent } from '@/lib/event-tracking';
import { useAiFoodOrderStore, useAuthStore, useLocationStore } from '@/store';
import { DELIVERY_TIME_SLOTS, useAIPlannerStore } from '@/store/ai-planner';

// eslint-disable-next-line sonarjs/cognitive-complexity
export function StepConfirmReminder() {
  const setPreferences = useAIPlannerStore.use.setPreferences();
  const currentPlan = useAIPlannerStore.use.currentPlan();
  const address = useAiFoodOrderStore.use.address();
  const { closeSheet } = useAIPlannerSheet();
  const { saveConfig } = useSaveReminderConfig();
  const user = useAuthStore.use.user();

  const [showTooltip, setShowTooltip] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<string>('');
  // keyword drives both the input value and the autocomplete query
  const [keyword, setKeyword] = useState(
    address?.short_address ?? address?.address ?? ''
  );
  // true on mount so autocomplete doesn't fire before the user types
  const [preventAutocomplete, setPreventAutocomplete] = useState(true);
  const [loadingAddressDetail, setLoadingAddressDetail] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const detectLocation = useLocationStore.use.detectLocation();
  const loadingLocation = useLocationStore.use.loading();

  const [debouncedKeyword] = useDebouncedValue(keyword, 500);

  const { data: addressOptions, isLoading: loadingAutocomplete } =
    useAutocomplete(
      { input: debouncedKeyword },
      {
        queryKey: ['autocomplete', { input: debouncedKeyword }],
        enabled: Boolean(
          debouncedKeyword &&
            debouncedKeyword.length >= 2 &&
            !preventAutocomplete
        ),
        refetchOnWindowFocus: false,
      }
    );

  const isLoadingSearch = loadingAutocomplete || loadingAddressDetail;

  const shouldShowSuggestions = Boolean(
    !loadingAddressDetail &&
      !preventAutocomplete &&
      debouncedKeyword &&
      addressOptions &&
      addressOptions.length > 0
  );

  const shouldShowEmpty = Boolean(
    !isLoadingSearch &&
      !preventAutocomplete &&
      debouncedKeyword &&
      debouncedKeyword.length >= 2 &&
      addressOptions &&
      addressOptions.length === 0
  );

  // Compute first draft order date from plan
  const firstOrderDate = currentPlan?.weeks?.[0]?.days?.[0]?.date;
  const firstOrderDateFormatted = firstOrderDate
    ? format(new Date(firstOrderDate), 'EEEE, dd.MM.yyyy', {
        locale: vi,
      }).toUpperCase()
    : null;

  const recipientName = user?.name ?? address?.contact_name ?? '';
  const recipientPhone = user?.phoneNumber ?? address?.contact_number ?? '';

  const canConfirm = keyword.trim().length > 0 && selectedSlot.length > 0;

  const handleUseCurrentLocation = useCallback(() => {
    setPreventAutocomplete(true);
    let isCompleted = false;

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        if (!isCompleted) {
          useLocationStore.setState({
            loading: false,
            error: new Error('Timeout'),
          });
          reject(new Error('Timeout'));
        }
      }, 5000);
    });

    Promise.race([detectLocation(), timeoutPromise])
      .then((response) => {
        isCompleted = true;
        const displayText =
          response.short_address ?? response.address?.split(',')[0] ?? '';
        setKeyword(displayText);
        setPreventAutocomplete(true);
      })
      .catch((error) => {
        isCompleted = true;
        console.error(error);
        setPreventAutocomplete(false);
        useLocationStore.setState({ loading: false });
        toast.error('Vui lòng cho phép bật vị trí trên trình duyệt này');
      });
  }, [detectLocation]);

  const applySelectedAddress = useCallback((selected: Address) => {
    const displayText =
      selected.short_address ?? selected.address?.split(',')[0] ?? '';
    setKeyword(displayText);
    setPreventAutocomplete(true);
  }, []);

  const handleAddressSelect = useCallback(
    async (selected: Address) => {
      if (selected.lat && selected.lng) {
        applySelectedAddress(selected);
        return;
      }

      if (selected.place_id) {
        setLoadingAddressDetail(true);
        setKeyword(selected.address ?? '');
        try {
          const res = await getGoogleAddressFn({
            place_id: selected.place_id,
            sessiontoken: selected.sessiontoken,
          });
          if (res) {
            applySelectedAddress(res);
          } else {
            toast.error('Không tìm được vị trí, vui lòng thử lại');
          }
        } catch {
          toast.error('Đã có lỗi khi chọn địa chỉ, vui lòng thử lại');
        } finally {
          setLoadingAddressDetail(false);
        }
        return;
      }

      applySelectedAddress(selected);
    },
    [applySelectedAddress]
  );

  const handleClearKeyword = useCallback(() => {
    setKeyword('');
    setPreventAutocomplete(false);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!canConfirm) return;

    setIsSaving(true);
    try {
      setPreferences({ deliveryTimeSlot: selectedSlot });
      await saveConfig({
        deliveryTimeSlot: selectedSlot,
        address: keyword,
        reminderStatus: 'ON',
      });
      trackEvent('meal_plan', {
        event_name: 'click_button',
        button_name: 'meal_plan_confirm_reminder',
        time_slot: selectedSlot,
      });
      closeSheet();
      toast.success(
        'Đã bật nhắc đặt bữa trưa mỗi ngày! Bạn sẽ nhận thông báo trước 11:00. Đơn chỉ được tạo khi bạn xác nhận.'
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    canConfirm,
    selectedSlot,
    keyword,
    setPreferences,
    saveConfig,
    closeSheet,
  ]);

  return (
    <div className="flex flex-col h-full">
      <ScrollArea className="flex-1 max-h-[calc(100dvh-123px-88px-24px)] overflow-auto scrollbar-hide">
        <div className="flex flex-col gap-4 pb-6">
          {/* Title */}
          <div className="mt-2">
            <h2 className="text-white font-bold text-xl leading-tight">
              Xác nhận cài đặt nhắc đặt bữa trưa
            </h2>
            {firstOrderDateFormatted && (
              <p className="text-white/50 text-xs mt-1">
                Đơn nháp đầu tiên:{' '}
                <span className="text-white/70 font-medium">
                  {firstOrderDateFormatted}
                </span>
              </p>
            )}
          </div>

          {/* Info banner */}
          <div className="relative flex items-center justify-center pt-4 h-28">
            {/* Mascot bên trái */}
            <div className="absolute -left-6 top-1/2 -translate-y-1/2 z-20">
              <Image
                src={`${assetPrefix}/static/images/ai-planner-tab-icon.webp`}
                alt="AI Planner Banner"
                width={140}
                height={140}
                className="object-contain scale-[1.6]"
              />
            </div>

            {/* Banner */}
            <div
              className="
              flex flex-col items-start gap-0
              h-fit
              ml-4
              mb-6
              pl-24 pr-8
              py-2
              rounded-full
              bg-gradient-to-r from-[#FF7A45] to-[#FF6A3D]
            "
            >
              {/* text */}
              <span className="text-white text-xs md:text-base font-semibold leading-none">
                8:00 AM mỗi ngày, AI tạo đơn nháp.
              </span>
              <span className="text-white text-xs md:text-base font-semibold leading-none mt-0.5">
                <span className="font-bold">Xác nhận </span>
                trước 11:00 AM mỗi ngày, bạn nhận thông báo xác nhận đặt món.
              </span>
            </div>
          </div>

          {/* Delivery address */}
          <div>
            <p className="text-white text-sm font-semibold mb-2 flex items-center gap-1.5">
              <MapPin className="size-4 text-[#006088]" />
              Giao món đến:
            </p>

            <div className="relative">
              <div className="flex items-center rounded-xl px-4 py-3 gap-2 ai-planner-input bg-white/5">
                <Input
                  value={keyword}
                  onChange={(e) => {
                    setKeyword(e.target.value);
                    setPreventAutocomplete(false);
                  }}
                  className="flex-1 bg-transparent border-none p-0 text-white placeholder:text-white/40 focus-visible:ring-0 focus-visible:ring-offset-0 h-auto text-sm"
                  placeholder="Nhập địa chỉ giao hàng"
                />
                {isLoadingSearch ? (
                  <Loader2 className="size-4 text-white/40 animate-spin shrink-0" />
                ) : keyword ? (
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleClearKeyword();
                    }}
                    className="shrink-0 text-white/40 hover:text-white transition-colors"
                  >
                    <X className="size-4" />
                  </button>
                ) : null}
              </div>

              {/* Use current location button */}
              {!shouldShowSuggestions && !shouldShowEmpty && (
                <button
                  type="button"
                  onClick={handleUseCurrentLocation}
                  disabled={loadingLocation || loadingAddressDetail}
                  className="flex items-center gap-1.5 mt-3 p-2 bg-white/5 ai-planner-input text-xs text-white font-medium disabled:opacity-50 rounded-xl"
                >
                  {loadingLocation ? (
                    <Loader2 className="size-3.5 animate-spin shrink-0" />
                  ) : (
                    <LocateFixed className="size-3.5 shrink-0" />
                  )}
                  {loadingLocation
                    ? 'Đang tìm vị trí...'
                    : 'Sử dụng vị trí hiện tại'}
                </button>
              )}

              {/* Suggestions dropdown — scrollable, max 3 items visible */}
              {shouldShowSuggestions && (
                <div className="absolute z-50 inset-x-0 mt-1 bg-white/5 backdrop-blur-md border ai-planner-input rounded-xl overflow-hidden shadow-2xl max-h-80 overflow-y-auto">
                  {addressOptions?.map((suggestion, index) => (
                    <button
                      key={suggestion.place_id ?? index}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        void handleAddressSelect(suggestion);
                      }}
                      className={cn(
                        'flex items-start gap-3 px-4 py-3 w-full text-left hover:bg-white/10 transition-colors',
                        index < (addressOptions?.length ?? 0) - 1 &&
                          'border-b border-white/10'
                      )}
                    >
                      <MapPin className="size-4 text-white/40 shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">
                          {suggestion.address?.split(',')[0]}
                        </p>
                        <p className="text-white/50 text-xs line-clamp-1">
                          {suggestion.address?.split(',').slice(1).join(',')}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Empty state */}
              {shouldShowEmpty && (
                <div className="absolute z-50 inset-x-0 mt-1 bg-transparent backdrop-blur-md border border-white/20 rounded-xl p-4 text-center shadow-2xl">
                  <p className="text-white/50 text-sm">
                    Không tìm thấy địa chỉ phù hợp
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Recipient */}
          {(recipientName || recipientPhone) && (
            <div className="flex items-center gap-3 rounded-xl">
              <User className="size-4 text-white/50 shrink-0" />
              <span className="text-white font-bold">Nguời nhận</span>
              <span className="flex-1 text-white/80 text-sm">
                <span className="font-medium text-white">{recipientName}</span>
                {recipientName && recipientPhone && (
                  <span className="text-white/50"> | </span>
                )}
                {recipientPhone}
              </span>
              {/* Tooltip for recipient info */}
              <TooltipProvider>
                <Tooltip open={showTooltip} onOpenChange={setShowTooltip}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => setShowTooltip((v) => !v)}
                      className="focus:outline-none"
                      tabIndex={-1}
                    >
                      <Info className="size-4 text-white/40 shrink-0" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    align="end"
                    className="max-w-xs text-xs text-white bg-[#01375f] border border-white/20 rounded-xl p-3 shadow-lg"
                  >
                    Thông tin mặc định được đồng bộ theo tài khoản. Để chỉnh sửa
                    vui lòng đăng nhập bằng tài khoản khác.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}

          {/* Time slot */}
          <div className="">
            <div className="flex items-start mb-3 gap-2">
              <Image
                src={`${assetPrefix}/static/icons/ai-planner-timer.svg`}
                alt="timer"
                width={20}
                height={20}
                className=""
              />
              <div className="flex flex-col">
                <p className="text-white text-sm font-semibold mb-0.5 flex items-center gap-1.5">
                  Giờ nhận hàng cho bữa trưa:
                </p>
                <p className="text-white/50 text-xs mb-3">
                  Thời gian mong muốn tài xế giao bữa trưa đến bạn
                </p>
              </div>
            </div>
            <RadioGroup
              value={selectedSlot}
              onValueChange={setSelectedSlot}
              className="flex flex-col gap-2 ai-planner-input rounded-xl bg-white/5 p-2"
            >
              {DELIVERY_TIME_SLOTS.map((slot) => {
                const isSelected = selectedSlot === slot.value;
                return (
                  <label
                    key={slot.value}
                    htmlFor={`slot-${slot.value}`}
                    className={cn(
                      'flex items-center gap-3 px-4 py-3.5 rounded-xl cursor-pointer transition-colors',
                      isSelected
                        ? 'bg-white/20 border border-white/30'
                        : 'bg-transparent border border-transparent'
                    )}
                  >
                    <Sun className="size-4 text-white/60 shrink-0" />
                    <span className="flex-1 text-white text-sm font-medium">
                      {slot.label}
                    </span>
                    <RadioGroupItem
                      id={`slot-${slot.value}`}
                      value={slot.value}
                      className={cn(
                        'size-5 shrink-0 border-2 transition-colors',
                        isSelected
                          ? 'border-[#FF8200] text-[#FF8200]'
                          : 'border-white/40 text-white/40'
                      )}
                    />
                  </label>
                );
              })}
            </RadioGroup>
          </div>

          <div className="h-20" />
        </div>
      </ScrollArea>

      {/* Fixed CTA */}
      <div className="fixed bottom-0 inset-x-0 pb-4 pt-2 px-4">
        <div className="glass-bubble rounded-full flex justify-center px-4 py-2 h-16">
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm || isSaving}
            className="size-full rounded-full bg-primary hover:bg-primary/90 text-white font-semibold text-base disabled:opacity-50"
          >
            Xác nhận thông tin tạo đơn mỗi ngày
          </Button>
        </div>
      </div>
    </div>
  );
}
